import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameCard } from "@/components/SportsCards";
import { EmptyState } from "@/components/brand";
import { Skeleton } from "@/components/ui/skeleton";

const CATS = { casino: ["all", "slots", "table", "dice"], live: ["all", "roulette", "blackjack", "baccarat", "cards"] };

export default function Casino({ section = "casino" }) {
  const { user } = useAuth();
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [games, setGames] = useState(null);
  const [favs, setFavs] = useState([]);
  const [onlyFav, setOnlyFav] = useState(false);

  useEffect(() => { setCat("all"); }, [section]);
  useEffect(() => {
    setGames(null);
    const t = setTimeout(() => api.get("/games", { params: { section, category: cat, q: q || undefined } }).then((r) => setGames(r.data)).catch(() => setGames([])), 250);
    return () => clearTimeout(t);
  }, [section, cat, q]);
  useEffect(() => { if (user) api.get("/me/favorites").then((r) => setFavs(r.data)); }, [user]);

  const toggleFav = async (id) => {
    if (!user) return toast.info("Log in to save favourites");
    const { data } = await api.post(`/me/favorites/${id}`);
    setFavs(data);
  };
  const featured = games?.filter((g) => g.tags.includes("featured")) || [];
  const shown = (games || []).filter((g) => !onlyFav || favs.includes(g.id));

  return (
    <main data-testid={`${section}-page`} className="mx-auto max-w-7xl px-5 py-10">
      <h1 className="text-4xl font-black uppercase">{section === "live" ? <>Live <span className="gold-text">Casino</span></> : <>Casino <span className="gold-text">Games</span></>}</h1>
      <p className="mt-2 max-w-2xl text-sm text-cr-muted">{section === "live" ? "Live tables with instant rounds. Pick a table and play with your coins." : "Slots, table games and dice. Tap any game to play with your coins."}</p>
      {featured.length > 0 && (
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {featured.map((g) => (
            <div key={g.id} data-testid={`featured-${g.id}`} className="relative h-48 overflow-hidden rounded-2xl border border-cr-gold/40">
              <img src={g.image} alt={g.name} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-cr-gold">Featured</p>
                <p className="mt-2 font-display text-2xl font-extrabold">{g.name}</p>
                <p className="text-sm text-cr-ink/70">{g.provider}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="my-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="scrollbar-none flex gap-2 overflow-x-auto">
          {CATS[section].map((c) => (
            <button key={c} data-testid={`casino-cat-${c}`} onClick={() => setCat(c)} className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold capitalize ${cat === c ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line text-cr-ink/80"}`}>{c}</button>
          ))}
          {user && <button data-testid="casino-fav-filter" onClick={() => setOnlyFav(!onlyFav)} className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold ${onlyFav ? "border-cr-gold text-cr-gold" : "border-cr-line text-cr-ink/80"}`}>Favourites</button>}
        </div>
        <div className="relative md:w-72"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" />
          <input data-testid="casino-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search games or providers" className="input-cr pl-10" /></div>
      </div>
      {games === null ? <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-56 bg-cr-elevated" />)}</div> :
        shown.length === 0 ? <EmptyState title="No games found" text="Try another category or search term." /> :
          <div data-testid="games-grid" className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">{shown.map((g) => <GameCard key={g.id} game={g} fav={favs.includes(g.id)} onFav={toggleFav} />)}</div>}
    </main>
  );
}
